import React from "react";
import { Heart, MapPin, Mail, Phone, ArrowUp } from "lucide-react";

const Stitch = ({ className = "", color = "#E0A526" }) => (
  <svg className={className} viewBox="0 0 100 4" preserveAspectRatio="none">
    <line
      x1="0" y1="2" x2="100" y2="2"
      stroke={color} strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
    />
  </svg>
);

const Footer = () => {
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-[#082440] text-blue-100/60 pt-16 pb-8 border-t border-white/10 relative overflow-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
        .mbf-display { font-family: 'Fraunces', serif; }
        .mbf-body { font-family: 'IBM Plex Sans', sans-serif; }
        .mbf-mono { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      {/* subtle woven-grid texture */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 34px), repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 34px)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 pb-12 border-b border-white/10">
          
          {/* Logo & Description */}
          <div className="flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3">
                <img
                  src="/logo.png"
                  alt="MB Foundation Logo"
                  className="h-9 w-auto "
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <div className="leading-tight mbf-body">
                  <h3 className="mbf-display text-white font-semibold text-base">MBFSDL</h3>
                  <p className="mbf-mono text-[9px] text-blue-100/40 uppercase tracking-widest font-semibold mt-0.5">
                    Skill &amp; Livelihood Trust
                  </p>
                </div>
              </div>
              <p className="mbf-body mt-6 text-sm text-blue-100/60 leading-relaxed">
                M.B. Foundation of Skill Development &amp; Livelihood (MBFSDL) is a registered charitable public trust (est. 18 September 2015). We serve communities without any profit motive.
              </p>
            </div>
            <div className="mbf-mono mt-8 text-[11px] text-blue-100/40 uppercase tracking-wider">
              Vellore, Tamil Nadu, India.
            </div>
          </div>

          {/* Quick Links */}
          <div className="mbf-body">
            <h4 className="mbf-display text-white font-semibold text-base mb-6">Quick Links</h4>
            <ul className="space-y-4 text-sm">
              <li>
                <a href="#home" className="hover:text-[#E0A526] transition-colors">Home</a>
              </li>
              <li>
                <a href="#about" className="hover:text-[#E0A526] transition-colors">About Us</a>
              </li>
              <li>
                <a href="#objectives" className="hover:text-[#E0A526] transition-colors">Objectives</a>
              </li>
              <li>
                <a href="#programs" className="hover:text-[#E0A526] transition-colors">Our Programs</a>
              </li>
              <li>
                <a href="#impact" className="hover:text-[#E0A526] transition-colors">Our Impact</a>
              </li>
            </ul>
          </div>

          {/* Contact Details */}
          <div className="mbf-body">
            <h4 className="mbf-display text-white font-semibold text-base mb-6">Registered Office</h4>
            <ul className="space-y-4 text-sm leading-relaxed">
              <li className="flex gap-3 items-start">
                <MapPin className="w-5 h-5 text-[#E0A526] shrink-0 mt-0.5" />
                <span>
                  No. 2/116 C, First Street,<br />
                  Samy Nagar, Bagayam,<br />
                  Vellore – 632002, Tamil Nadu
                </span>
              </li>
              <li className="flex gap-3 items-center">
                <Mail className="w-5 h-5 text-[#E0A526] shrink-0" />
                <a href="mailto:contact@mbfoundationsdl.org" className="hover:text-[#E0A526] transition-colors">
                  contact@mbfoundationsdl.org
                </a>
              </li>
              <li className="flex gap-3 items-center">
                <Phone className="w-5 h-5 text-[#E0A526] shrink-0" />
                <a href="tel:+91 7208058502" className="hover:text-[#E0A526] transition-colors">
                  +91  7208058502
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Declarations */}
          <div className="mbf-body flex flex-col justify-between items-start">
            <div>
              <h4 className="mbf-display text-white font-semibold text-base mb-6">Declarations</h4>
              <p className="text-sm text-blue-100/60 leading-relaxed mb-4">
                MBFSDL functions as a non-profit, non-political, and voluntary organization. All initiatives are executed without any profit motives.
              </p>
            </div>
            <div className="w-full">
              <Stitch className="w-20 h-2 mb-4" color="#E0A526" />
              <button
                onClick={handleScrollToTop}
                className="mbf-mono mt-2 flex items-center gap-2 text-[10px] font-semibold text-[#E0A526] hover:text-white transition-colors uppercase tracking-widest group"
              >
                Back to Top
                <ArrowUp className="w-3.5 h-3.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </div>
          </div>

        </div>

        {/* Bottom copyright */}
        <div className="flex flex-col sm:flex-row justify-between items-center mt-12 text-blue-100/40 gap-4">
          <p className="mbf-mono text-[11px] uppercase tracking-wider">
            © {new Date().getFullYear()} M.B. Foundation of Skill Development &amp; Livelihood.
          </p>
          {/* <p className="mbf-body text-xs flex items-center gap-1.5">
            Made with <Heart className="w-3.5 h-3.5 text-[#2E7D32] fill-[#2E7D32]" /> for community empowerment
          </p> */}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
